import { PROVIDER_WIN_KEYS } from '../ai/keys';
import { API_BASE } from '../api-base';
import { decrypt, deriveKey, encryptWithKey } from '../crypto';
import type { AIProviderName } from '../types';
import { getLimit, getUsage } from '../usage';
import { escapeHtml } from './escape';
import { readUseCardImagesPref } from './reading-block';

// ── Storage key constants (single source of truth) ───────────────────────────

// Per-provider storage / DOM identifiers. Single table — adding a provider
// is one entry here plus the AIProviderName union update. Replaces the four
// parallel maps (PLAIN_KEY/ENC_KEY/INPUT_ID/STORAGE_KEYS provider entries)
// that used to drift independently.
const PROVIDER_META: Record<AIProviderName, { plain: string; enc: string; inputId: string }> = {
  gemini: {
    plain: 'linyuan.geminiKey',
    enc: 'linyuan.geminiKey.enc',
    inputId: 'gemini-key-input',
  },
  groq: {
    plain: 'linyuan.groqKey',
    enc: 'linyuan.groqKey.enc',
    inputId: 'groq-key-input',
  },
  openai: {
    plain: 'linyuan.openaiKey',
    enc: 'linyuan.openaiKey.enc',
    inputId: 'openai-key-input',
  },
  claude: {
    plain: 'linyuan.claudeKey',
    enc: 'linyuan.claudeKey.enc',
    inputId: 'claude-key-input',
  },
  volcengine: {
    plain: 'linyuan.volcengineKey',
    enc: 'linyuan.volcengineKey.enc',
    inputId: 'volcengine-key-input',
  },
};

const PROVIDERS = Object.keys(PROVIDER_META) as AIProviderName[];

// Cross-cutting (non-provider) storage keys. Exported because client.ts
// (lastProvider) and home.ts (user) read them directly.
export const STORAGE_KEYS = {
  passphrase: 'linyuan.passphrase',
  user: 'linyuan.userName',
  lastProvider: 'linyuan.lastProvider',
} as const;

// ── Helpers ───────────────────────────────────────────────────────────────────

type Win = Record<string, string | undefined>;

function winSet(globalKey: string, value: string): void {
  (window as unknown as Win)[globalKey] = value;
}

function winDel(globalKey: string): void {
  delete (window as unknown as Win)[globalKey];
}

function winGet(globalKey: string): string | undefined {
  return (window as unknown as Win)[globalKey];
}

export function clearAllStoredKeys(): void {
  for (const p of PROVIDERS) {
    localStorage.removeItem(PROVIDER_META[p].plain);
    localStorage.removeItem(PROVIDER_META[p].enc);
    winDel(PROVIDER_WIN_KEYS[p]);
  }
  sessionStorage.removeItem(STORAGE_KEYS.passphrase);
}

// ── Restore keys on load (encrypted-first, plain fallback, 3-attempt retry) ──

export async function restoreKeys(): Promise<void> {
  const hasEnc = PROVIDERS.some((p) => localStorage.getItem(PROVIDER_META[p].enc) !== null);

  if (hasEnc) {
    let passphrase: string | null = sessionStorage.getItem(STORAGE_KEYS.passphrase);
    let prompted = 0;

    while (prompted < 3) {
      if (!passphrase) {
        passphrase = prompt(
          '临渊 · Lin Yuan\n\nEnter passphrase to unlock your encrypted API key(s):\n输入密码以解锁您加密的 API 密钥：',
        );
        if (!passphrase) break; // cancelled — fall through to plain fallback
        prompted++;
      }

      let ok = true;
      for (const p of PROVIDERS) {
        const blob = localStorage.getItem(PROVIDER_META[p].enc);
        if (!blob) continue;
        try {
          winSet(PROVIDER_WIN_KEYS[p], await decrypt(blob, passphrase));
        } catch (e) {
          console.warn(`${p} key decrypt failed:`, (e as Error).message);
          ok = false;
        }
      }

      if (ok) {
        sessionStorage.setItem(STORAGE_KEYS.passphrase, passphrase);
        // Mixed-state safety: if a provider has a plain blob but no
        // encrypted blob (e.g., partial migration), the encrypted-decrypt
        // loop above didn't load it. Fill in any plain keys for providers
        // that didn't get a window value from the decrypt pass.
        loadPlainKeysForUnsetProviders();
        return;
      }

      // Clean up partial decrypts
      for (const p of PROVIDERS) winDel(PROVIDER_WIN_KEYS[p]);
      sessionStorage.removeItem(STORAGE_KEYS.passphrase);
      passphrase = null;
    }

    if (prompted >= 3) {
      alert('Wrong passphrase 3 times — running in static mode.\n密码错误 3 次——以静读模式运行。');
      return;
    }
    // User cancelled — fall through to plain fallback below
  }

  // Plain fallback (also reached on cancel above)
  loadPlainKeysForUnsetProviders();
}

function loadPlainKeysForUnsetProviders(): void {
  for (const p of PROVIDERS) {
    if (winGet(PROVIDER_WIN_KEYS[p])) continue;
    const v = localStorage.getItem(PROVIDER_META[p].plain);
    if (v) winSet(PROVIDER_WIN_KEYS[p], v);
  }
}

// ── Panel render helpers ──────────────────────────────────────────────────────

// Providers whose Worker secret is set, fetched from /api/ai/providers on mount.
let serverProviders: Set<AIProviderName> = new Set();

async function loadServerProviders(panel: HTMLElement): Promise<void> {
  try {
    const r = await fetch(`${API_BASE}/api/ai/providers`);
    if (!r.ok) {
      console.warn(`/api/ai/providers returned ${r.status} — status row will show no yellow ●`);
      return;
    }
    const j = (await r.json()) as { available?: AIProviderName[] };
    serverProviders = new Set(j.available ?? []);
    renderStatus(panel);
  } catch (e) {
    console.warn('/api/ai/providers fetch failed:', (e as Error).message);
  }
}

function renderStatus(panel: HTMLElement): void {
  const u = getUsage();
  const lastProvider = localStorage.getItem(STORAGE_KEYS.lastProvider);
  const row = PROVIDERS.map((p) => {
    const hasByok = !!winGet(PROVIDER_WIN_KEYS[p]);
    const hasServer = serverProviders.has(p);
    const has = hasByok || hasServer;
    const isActive = p === lastProvider;
    // No key anywhere AND not the last-used provider → muted dash.
    if (!has && !isActive) return `<span class="sp-st-muted">${p} —</span>`;
    // Daily counters only track BYOK usage (server quota lives elsewhere),
    // so the over-limit ⊘ + counter only applies when the key is BYOK.
    const used = hasByok ? u[p] : 0;
    const lim = getLimit(p);
    const over = hasByok && used >= lim;
    const counter = hasByok ? ` ${used}/${lim}` : '';
    // Glyph + colour rule:
    //   over BYOK limit → red ⊘
    //   currently in use (last successful provider) → green ✓
    //   key configured (BYOK or server-side) but idle → yellow ●
    if (over) return `<span class="sp-st-over">${p} ⊘${counter}</span>`;
    if (isActive) return `<span class="sp-st-active">${p} ✓${counter}</span>`;
    return `<span class="sp-st-idle">${p} ●${counter}</span>`;
  }).join(' · ');

  const statusEl = panel.querySelector('#ai-status-line');
  if (!statusEl) return;
  statusEl.innerHTML =
    `<div style="margin-bottom:4px">${row}</div>` +
    `<div style="font-size:10px;opacity:0.5;margin-top:4px">` +
    `<span lang="en">Daily counters reset at midnight (local). Providers auto-stop near free-tier limits.</span>` +
    `<span lang="zh">每日计数在本地午夜重置。接近免费额度时自动停用。</span>` +
    `</div>`;
}

function refreshPanel(panel: HTMLElement): void {
  // Inputs only exist in the dev variant of the panel. Guard each lookup
  // so refresh is a no-op on the prod status-only panel.
  const userInput = panel.querySelector<HTMLInputElement>('#user-name-input');
  if (userInput) userInput.value = localStorage.getItem(STORAGE_KEYS.user) ?? '';

  for (const p of PROVIDERS) {
    const el = panel.querySelector<HTMLInputElement>(`#${PROVIDER_META[p].inputId}`);
    if (el) el.value = localStorage.getItem(PROVIDER_META[p].plain) ?? '';
  }

  const passInput = panel.querySelector<HTMLInputElement>('#passphrase-input');
  if (passInput) passInput.value = '';

  renderStatus(panel);
}

// ── Save ──────────────────────────────────────────────────────────────────────

// In-flight guard against rapid double-click. Two concurrent saves can each
// derive a fresh salt, encrypt, and clear-then-write — leaving torn storage
// where some providers' encrypted blobs come from one run and some from the
// other (different salts → only one passphrase decrypts each subset, breaking
// restoreKeys).
let saveInFlight = false;

async function saveAllKeys(panel: HTMLElement): Promise<void> {
  if (saveInFlight) return;
  saveInFlight = true;
  const saveBtn = panel.querySelector<HTMLButtonElement>('#save-keys-btn');
  if (saveBtn) saveBtn.disabled = true;
  try {
    await saveAllKeysInner(panel);
  } finally {
    saveInFlight = false;
    if (saveBtn) saveBtn.disabled = false;
  }
}

async function saveAllKeysInner(panel: HTMLElement): Promise<void> {
  const passEl = panel.querySelector<HTMLInputElement>('#passphrase-input');
  const userEl = panel.querySelector<HTMLInputElement>('#user-name-input');
  const statusEl = panel.querySelector('#ai-status-line');
  if (!passEl || !userEl || !statusEl) return;
  const pass = passEl.value;

  // Atomic write: do all the work that can throw FIRST (collect inputs,
  // derive key, encrypt). Only after every byte is ready do we wipe the
  // existing storage and write the new state. A failure mid-encrypt
  // therefore leaves the user's previously persisted keys intact.
  try {
    const entries = PROVIDERS.flatMap((p) => {
      const el = panel.querySelector<HTMLInputElement>(`#${PROVIDER_META[p].inputId}`);
      const v = el?.value.trim() ?? '';
      return v ? [{ p, v }] : [];
    });

    let stagedCiphertexts: string[] | null = null;
    let stagedSalt: Uint8Array | null = null;
    if (pass && entries.length > 0) {
      stagedSalt = crypto.getRandomValues(new Uint8Array(16));
      const key = await deriveKey(pass, stagedSalt);
      const ivs = entries.map(() => crypto.getRandomValues(new Uint8Array(12)));
      stagedCiphertexts = await Promise.all(
        entries.map(({ v }, i) =>
          encryptWithKey(v, key, stagedSalt as Uint8Array, ivs[i] as Uint8Array),
        ),
      );
    }

    // Crypto succeeded (or wasn't needed). Now and only now do we clear
    // and write — so a failure above can't leave the user with no keys.
    localStorage.setItem(STORAGE_KEYS.user, userEl.value.trim());
    clearAllStoredKeys();

    if (stagedCiphertexts) {
      for (let i = 0; i < entries.length; i++) {
        const e = entries[i];
        if (!e) continue;
        localStorage.setItem(PROVIDER_META[e.p].enc, stagedCiphertexts[i] as string);
        winSet(PROVIDER_WIN_KEYS[e.p], e.v);
      }
      sessionStorage.setItem(STORAGE_KEYS.passphrase, pass);
    } else {
      for (const { p, v } of entries) {
        localStorage.setItem(PROVIDER_META[p].plain, v);
        winSet(PROVIDER_WIN_KEYS[p], v);
      }
    }
  } catch (err) {
    const msg = escapeHtml((err as Error).message);
    statusEl.innerHTML = `<span style="color:#d66">✗ <span lang="en">Encryption failed: ${msg}</span><span lang="zh">加密失败：${msg}</span></span>`;
    return;
  }

  const lock = pass ? ' 🔒' : '';
  statusEl.innerHTML = `<span style="color:#8fe0a4">✓${lock} <span lang="en">Saved${pass ? ' (encrypted)' : ''}.</span><span lang="zh">已保存${pass ? '（已加密）' : ''}。</span></span>`;

  // Clear any in-flight Save-confirm timer before re-arming. Otherwise clicking
  // Clear within 1.6s of Save closes the panel out from under the Clear render.
  if (saveConfirmTimer) clearTimeout(saveConfirmTimer);
  saveConfirmTimer = setTimeout(() => {
    renderStatus(panel);
    panel.style.display = 'none';
    saveConfirmTimer = null;
  }, 1600);
}

let saveConfirmTimer: ReturnType<typeof setTimeout> | null = null;

// ── Clear ─────────────────────────────────────────────────────────────────────

function clearAndClose(panel: HTMLElement): void {
  if (saveConfirmTimer) {
    clearTimeout(saveConfirmTimer);
    saveConfirmTimer = null;
  }
  clearAllStoredKeys();
  for (const p of PROVIDERS) {
    const el = panel.querySelector<HTMLInputElement>(`#${PROVIDER_META[p].inputId}`);
    if (el) el.value = '';
  }
  const passEl = panel.querySelector<HTMLInputElement>('#passphrase-input');
  if (passEl) passEl.value = '';
  const userEl = panel.querySelector<HTMLInputElement>('#user-name-input');
  if (userEl) userEl.value = '';
  renderStatus(panel);
  panel.style.display = 'none';
}

// ── Mount ─────────────────────────────────────────────────────────────────────

let mounted = false;

export function mountSettingsPanel(parent: HTMLElement): void {
  // Guard against double-mount: HMR or accidental re-call would otherwise
  // register duplicate document-level keydown / click listeners.
  if (mounted) return;
  mounted = true;

  // Only restore keys (and the passphrase prompt that may follow) in dev —
  // prod has nothing in localStorage to decrypt.
  if (import.meta.env.DEV) void restoreKeys();

  const panel = document.createElement('div');
  panel.id = 'settings-panel';
  panel.style.display = 'none';

  // Dev panel adds the BYOK form; prod panel is status-only.
  const devForm = import.meta.env.DEV
    ? `
    <div class="sp-disclaimer">
      <span lang="en">Keys are stored in your browser only. Never sent anywhere except the API you configure.</span>
      <span lang="zh">密钥仅保存在本浏览器中，除你设置的 API 之外不会发送到其他地方。</span>
    </div>

    <label><span lang="en">Your name (optional)</span><span lang="zh">你的名字（可选）</span></label>
    <input id="user-name-input" type="text" placeholder="—" />

    <label>
      <span lang="en">Volcengine API key <span style="opacity:0.5">(works inside mainland China)</span></span>
      <span lang="zh">Volcengine 密钥 <span style="opacity:0.5">（中国大陆可用）</span></span>
    </label>
    <input id="volcengine-key-input" type="password" placeholder="ARK_..." />

    <label>
      <span lang="en">Groq API key <span style="opacity:0.5">(most generous free tier)</span></span>
      <span lang="zh">Groq 密钥 <span style="opacity:0.5">（免费额度最大）</span></span>
    </label>
    <input id="groq-key-input" type="password" placeholder="gsk_..." />

    <label><span lang="en">Gemini API key</span><span lang="zh">Gemini 密钥</span></label>
    <input id="gemini-key-input" type="password" placeholder="AIzaSy..." />

    <label><span lang="en">Claude API key</span><span lang="zh">Claude 密钥</span></label>
    <input id="claude-key-input" type="password" placeholder="sk-ant-..." />

    <label><span lang="en">ChatGPT (OpenAI) API key</span><span lang="zh">ChatGPT 密钥</span></label>
    <input id="openai-key-input" type="password" placeholder="sk-proj-..." />

    <label>
      <span lang="en">Passphrase <span style="opacity:0.5">(optional — encrypts keys at rest)</span></span>
      <span lang="zh">密码短语 <span style="opacity:0.5">（可选 · 用于加密密钥）</span></span>
    </label>
    <input id="passphrase-input" type="password" placeholder="leave empty to store plain" />

    <div class="sp-actions">
      <button id="save-keys-btn">
        <span lang="en">Save</span><span lang="zh">保存</span>
      </button>
      <button id="clear-keys-btn">
        <span lang="en">Clear</span><span lang="zh">清除</span>
      </button>
    </div>`
    : '';

  panel.innerHTML = `
    <div class="sp-heading">
      <span lang="en">Deep sea settings</span><span lang="zh">深海设置</span>
    </div>
    <div class="sp-toggle-row">
      <label>
        <input id="card-images-toggle" type="checkbox" />
        <span lang="en">Use real card images</span>
        <span lang="zh">使用真实卡牌图像</span>
      </label>
      <div class="sp-toggle-hint">
        <span lang="en">Applies to your next reading.</span>
        <span lang="zh">下次解读时生效。</span>
      </div>
    </div>
    ${devForm}
    <div id="ai-status-line"></div>
    ${import.meta.env.DEV ? '<div id="ai-error-line" class="sp-error"></div>' : ''}
  `;

  parent.appendChild(panel);

  // All listeners share this controller so an HMR-driven module re-eval can
  // detach the previous mount's listeners cleanly. Without it, every dev
  // save → reload accumulates a stale listener pointing at a detached panel.
  const listeners = new AbortController();
  const opts = { signal: listeners.signal };

  // ESC closes the panel
  document.addEventListener(
    'keydown',
    (e) => {
      if (e.key === 'Escape' && panel.style.display !== 'none') {
        panel.style.display = 'none';
      }
    },
    opts,
  );

  // Click outside closes the panel; click inside does not bubble out
  panel.addEventListener('click', (e) => e.stopPropagation(), opts);
  document.addEventListener(
    'click',
    (e) => {
      const target = e.target as HTMLElement;
      if (target.closest('#menu-btn')) {
        e.stopPropagation();
        refreshPanel(panel);
        panel.style.display = panel.style.display === 'none' ? 'block' : 'none';
        return;
      }
      if (!panel.contains(target)) {
        panel.style.display = 'none';
      }
    },
    opts,
  );

  // No reload on change — toggling mid-reading must not discard drawn
  // cards or in-flight AI text. Effect applies at the next reading start.
  // Default ON: '0' means the user explicitly opted out; absent means use
  // real images. Mirrors readUseCardImagesPref in reading-block.ts.
  const cardImgToggle = panel.querySelector<HTMLInputElement>('#card-images-toggle');
  if (cardImgToggle) {
    cardImgToggle.checked = readUseCardImagesPref();
    cardImgToggle.addEventListener(
      'change',
      () => {
        try {
          if (cardImgToggle.checked) localStorage.removeItem('useCardImages');
          else localStorage.setItem('useCardImages', '0');
        } catch {}
      },
      opts,
    );
  }

  // Status auto-refresh after every successful AI call. Wired in both dev and
  // prod since the status row exists in both — without this, the green ✓
  // wouldn't move to a different provider until the user re-opened the menu.
  window.addEventListener('linyuan:ai-state-change', () => renderStatus(panel), opts);

  if (import.meta.hot) {
    import.meta.hot.dispose(() => {
      listeners.abort();
      panel.remove();
      mounted = false;
    });
  }

  // Save / clear buttons + AI error listener — dev-only. In prod the form
  // doesn't exist (no save target) and we don't want to expose internal
  // upstream-error strings to end users.
  if (import.meta.env.DEV) {
    panel.querySelector('#save-keys-btn')?.addEventListener(
      'click',
      () => {
        void saveAllKeys(panel);
      },
      opts,
    );
    panel.querySelector('#clear-keys-btn')?.addEventListener(
      'click',
      () => {
        clearAndClose(panel);
      },
      opts,
    );

    window.addEventListener(
      'linyuan:ai-error',
      (e) => {
        const errLine = panel.querySelector<HTMLElement>('#ai-error-line');
        if (!errLine) return;
        // Defensive: CustomEvent constructor doesn't strict-check `init.detail`,
        // so a malformed dispatch can't be caught by the WindowEventMap typing.
        const when = Number.isFinite(e.detail?.when)
          ? new Date(e.detail.when).toLocaleTimeString()
          : '—';
        errLine.textContent = `[${when}] ${e.detail?.message ?? 'unknown error'}`;
      },
      opts,
    );
  }

  renderStatus(panel);

  // Probe Worker for which providers have server-side secrets configured —
  // re-renders the status row with yellow ● dots for any that are available
  // but idle (the user hasn't triggered a call from one yet).
  void loadServerProviders(panel);
}
